/**
 * Shared <LibraryDocumentsView scope> component (Library-PR-X3 / Phase 4).
 *
 * Pre-refactor:
 *   - routes/library/documents.tsx          (tenant,  1063 LOC)
 *   - routes/sysadmin/library.documents.tsx (sysadmin, 580 LOC)
 *
 * Both rendered the same conceptual surface (unified table + filter row +
 * bulk-select + "New" dropdown + empty state) but diverged on the
 * publish-lifecycle column, the bulk action, the star/share affordances,
 * and the tenant's KbSidebar/WikiEditor full-page modes.
 *
 * This component renders the SHARED list view. Scope-specific affordances
 * (sidebar, full-page viewer/editor, share dialogs, star toggles) stay
 * in the route files and are composed AROUND this view.
 *
 * Cutover affordances (founder-locked decision matrix)
 * ----------------------------------------------------
 * Path B — wired INTO the View body:
 *   #2/#3 Star column (`showStarColumn`): THE StarButton, kind 'document' (LB6-T5)
 *   #7    Share-doc dropdown menu item (`onShareRow`)
 *   #10   Multi-action bulk bar (`getBulkActions` returns 0..N buttons)
 *   #12   Sortable column headers — the columns the adapter's SOURCE can order
 *         by (`sortKeys`); the tenant's server orders the list (LB6-T2)
 *   #13   Pagination — "Show more (N remaining)" asks the source for the next
 *         page; N is the source's total less the rows loaded
 *
 * THE SOURCE ANSWERS (Library LB6-T2, L6-15 .. L6-19). The View holds the
 * controls — folder, type, status, text, order — and hands them ALL to the
 * adapter's `useList`; it renders the rows, counts and errors it is handed
 * and derives none of them: no client filter, sort or tally (the tenant
 * adapter asks GET /knowledge/documents; scripts/ci/no-web-derivation-lint.mjs
 * W6 holds this file to it). A failed read is an error state, never an
 * empty table or a 0.
 *
 * Path C — composed AROUND the View by the route file:
 *   #1  KbSidebar             (tenant route renders as flex sibling)
 *   #4  (read mode deleted — decision 43; articles open at /library/article)
 *   #5  WikiEditor (edit mode)(route handles viewMode === 'edit')
 *   #6  Types button          (route passes via `headerActions` slot)
 *   #8  Share-folder dialog   (route owns dialog, triggered from KbSidebar)
 *   #9  Move-to-folder dialog (route owns dialog; View fires the action via
 *                              the Move BulkActionDescriptor.onClick)
 *
 * Deferred:
 *   #11 Bulk Tag stub — dropped entirely (was a disabled button).
 *
 * Frozen invariant
 * ----------------
 * Per PR-B audit pattern (`feedback_shared_component_scope_prop.md`),
 * the adapter object is read-only inside this file — we never reassign
 * adapter fields. The route caller passes a stable adapter via useMemo;
 * the view body never tries to mutate it.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Plus, Search, Loader2, Trash2, MoreHorizontal, FileText,
  Upload, PenSquare, Edit, Share2, Eye, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { StarButton } from '@/components/library/star-button';
import { SortableHeader } from '@/components/ui/data-table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useWikiTags } from '@/lib/use-library-documents';
import { selectedKindsSet, selectionOnScreen, tagFilterInCatalogue, timeAgo } from './LibraryDocumentsView.helpers';
import {
  shouldRenderViewItem,
  shouldRenderDownloadItem,
  hasFailedIngest,
  buildDeleteIntent,
  type DeleteIntent,
} from './LibraryDocumentsView.misc-ddd-cluster';
import type {
  LibraryDocumentsAdapter, LibraryDocumentsScope, LibraryListSort, ListDocsQuery, NormalizedLibraryDoc,
} from './LibraryDocumentsView.types';
import { DOCUMENT_SEARCH_MAX_LENGTH, type DocumentKind } from '@argusai/shared';
import { DOCUMENT_KIND_FILTER_LABEL, DocumentKindBadge, DocumentKindIcon } from './document-kind';
import { DeleteConfirmDialog } from './delete-confirm-dialog';

/** How long the text filter waits after the last keystroke before it asks the source. */
export const SEARCH_SETTLE_MS = 300;

export interface LibraryDocumentsViewProps {
  scope: LibraryDocumentsScope;
  adapter: LibraryDocumentsAdapter;
  /**
   * Overrides the adapter's `pageTitle` (e.g. tenant uses the active
   * folder name when one is selected in the sidebar). When omitted the
   * adapter's `pageTitle` is rendered.
   */
  pageTitleOverride?: string;
  /**
   * #676 — Forces the folder filter to a specific value (typically the
   * active folder selected in the tenant sidebar). When set, the
   * in-header folder Select hides and the adapter's `useList` receives
   * this id as `folderFilter`. Pass `null` to mean "All Documents
   * (root)"; the value `'all'` (or undefined) means "no override —
   * user controls the in-header Select".
   *
   * Pre-fix the sidebar's selection only changed the page heading,
   * leaving the table unfiltered (LIB-DOC-FILTER-007). The override
   * threads the sidebar selection through to the list query so the
   * shown rows match the heading.
   */
  folderFilterOverride?: string | null;
  /**
   * Extra controls rendered to the LEFT of the New dropdown in the
   * header. The tenant route uses this for the "Types" button. Per the
   * founder-locked decision matrix this is a Path-C composition slot —
   * the route file owns the dialog state, the View just renders the
   * supplied node.
   */
  headerActions?: ReactNode;
}

export function LibraryDocumentsView({
  scope,
  adapter,
  pageTitleOverride,
  folderFilterOverride,
  headerActions,
}: LibraryDocumentsViewProps) {
  // Filters — every one is a parameter of the source's answer (L6-15).
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const settle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_SETTLE_MS);
    return () => clearTimeout(settle);
  }, [searchInput]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [folderFilter, setFolderFilter] = useState<string>('all');
  const [kindFilter, setKindFilter] = useState<'all' | DocumentKind>('all');
  // The Tag filter (decision 60, LB6c-T1) — only where the adapter offers it.
  const [tagIdFilter, setTagIdFilter] = useState<string>('all');
  const effectiveTagIdFilter = adapter.tagFilter ? tagIdFilter : 'all';

  // #676 — Effective folder filter sent to the adapter. The override
  // (when defined) wins over the in-header Select. null → root-only;
  // a uuid → that folder; undefined → fall back to the Select value.
  const effectiveFolderFilter = folderFilterOverride !== undefined
    ? (folderFilterOverride === null ? '__root__' : folderFilterOverride)
    : folderFilter;

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // #677 + #679 — Delete confirmation intent (null = no pending delete).
  // Set by row dropdown Delete + destructive bulk actions; cleared by
  // the AlertDialog Cancel + Confirm handlers.
  const [pendingDelete, setPendingDelete] = useState<DeleteIntent | null>(null);

  // Order (#12) — the source orders the list; a header click asks again.
  const [sort, setSort] = useState<LibraryListSort>(adapter.defaultSort);
  const handleSort = (key: string) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };
  const sortConfig = { key: sort.key, direction: sort.direction };

  // Data — the source's answer to everything above.
  const query: ListDocsQuery = {
    statusFilter,
    folderFilter: effectiveFolderFilter,
    kindFilter,
    ...(adapter.tagFilter ? { tagIdFilter: effectiveTagIdFilter } : {}),
    search,
    sort,
  };
  const { docs, counts, folders, loading, error, hasMore, loadingMore, loadMore, loadMoreError, retry } = adapter.useList(query);

  // A selection belongs to the list it was made in (LB6T2-BUG-09). A new
  // question — another folder, kind, status, text or order — is another list,
  // so the selection is dropped: a bulk action never reaches rows that are not
  // on screen. (Keyed on the question's fields; the object is rebuilt each render.)
  const listIdentity = JSON.stringify([effectiveFolderFilter, kindFilter, statusFilter, effectiveTagIdFilter, search, sort.key, sort.direction]);
  useEffect(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, [listIdentity]);
  // And only rows ON SCREEN stay selected: a row a write removed from the
  // list (a bulk delete, a move out of this folder) leaves the selection.
  const onScreen = useMemo(() => new Set(docs.map((d) => d.id)), [docs]);
  useEffect(() => {
    setSelectedIds((prev) => selectionOnScreen(prev, onScreen) as Set<string>);
  }, [onScreen]);

  // The rows on screen are every row loaded so far (#13 "Show more" loads the
  // next page from the source).
  const visible = docs;
  const remaining = counts ? Math.max(0, counts.total - docs.length) : null;
  const filtersActive = search.length > 0 || kindFilter !== 'all' || statusFilter !== 'all' || effectiveTagIdFilter !== 'all';

  // ── Selection helpers ─────────────────────────────────────────────────────
  const toggleRowSelected = (d: NormalizedLibraryDoc) => {
    if (!adapter.isSelectable(d)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(d.id)) next.delete(d.id);
      else next.add(d.id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // "Select all" applies only to the visible (page-sliced) selectable rows
  // — matches the pre-cutover tenant behaviour.
  const selectableVisible = visible.filter((d) => adapter.isSelectable(d));
  const allSelectedOnPage =
    selectableVisible.length > 0 &&
    selectableVisible.every((d) => selectedIds.has(d.id));

  const handleToggleAll = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) for (const d of selectableVisible) next.add(d.id);
      else for (const d of selectableVisible) next.delete(d.id);
      return next;
    });
  };

  // ── Bulk actions (#10 multi-action) ───────────────────────────────────────
  const selectedKinds = useMemo(
    () => selectedKindsSet(selectedIds, docs),
    [selectedIds, docs],
  );
  const selectedDocs = useMemo(
    () => docs.filter((d) => selectedIds.has(d.id)),
    [docs, selectedIds],
  );
  const bulkActions = adapter.getBulkActions({
    selectedIds,
    selectedDocs,
    selectedKinds,
  });
  // The first disabled action contributes its reason to the action-bar
  // helper text. Sysadmin's mixed-kinds-disabled case uses this.
  const firstDisabledReason =
    bulkActions.find((a) => a.disabled && a.disabledReason)?.disabledReason ?? null;

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" aria-busy="true" data-testid="library-list-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // A failed read is an ERROR — never "No documents", never a 0 (L6-17).
  if (error) {
    return (
      <div className="p-6">
        <div
          role="alert"
          data-testid="library-list-error"
          className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
        >
          Failed to load documents: {error.message}
          {retry ? (
            <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs" onClick={() => retry()} title="Load the documents again">
              Retry
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  // The New menu offers only what this person may create (L6-04): with
  // nothing to offer, there is no New button.
  const newExtras = adapter.newMenuExtras ?? [];
  const canCreateSomething = adapter.allows.upload || adapter.allows.writeArticle || newExtras.length > 0;
  // A row whose menu would hold nothing this person may use shows no menu.
  const rowHasActions = (d: NormalizedLibraryDoc): boolean =>
    shouldRenderViewItem(adapter, d) ||
    shouldRenderDownloadItem(adapter, d) ||
    adapter.allows.edit(d) ||
    (adapter.allows.publish(d) && (!!adapter.onPublishRow || !!adapter.onUnpublishRow)) ||
    !!adapter.onShareRow ||
    (!!adapter.onMoveRow && (d.kind === 'file' || d.kind === 'synced')) ||
    adapter.allows.delete(d);

  // A column header is sortable only when the source can order by it (#12):
  // a header that sorted only the loaded page would be a second order.
  const sortable = (label: string, key: string): ReactNode =>
    adapter.sortKeys.includes(key)
      ? <SortableHeader label={label} sortKey={key} currentSort={sortConfig} onSort={handleSort} />
      : label;

  // Total visible columns for the empty-state colspan.
  let colCount = 1 /* title */ + 1 /* type */ + 1 /* folder */
    + 1 /* status */ + 1 /* updated */ + 1 /* actions */;
  if (adapter.allows.bulkSelect) colCount += 1;
  if (adapter.showStarColumn) colCount += 1;
  if (adapter.showAuthorColumn) colCount += 1;

  return (
    <div className="p-6 space-y-4" data-scope={scope}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">{pageTitleOverride ?? adapter.pageTitle}</h2>
          <p className="text-xs text-muted-foreground" data-testid="library-list-stats">
            {/* #2 — the breakdown reflects the active filters: it is the
                source's counts for THIS query (the tenant's countsByKind,
                L6-15), never a tally of the loaded rows. No counts, no line. */}
            {counts ? adapter.buildStatsLine(counts) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {headerActions}
          {canCreateSomething && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-1.5" title="Create new document or article">
                <Plus className="h-3.5 w-3.5" /> New
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {adapter.allows.upload && (
                <DropdownMenuItem onClick={() => adapter.onOpenUpload()} className="text-xs">
                  <Upload className="h-3.5 w-3.5 mr-2" /> Upload File
                </DropdownMenuItem>
              )}
              {adapter.allows.writeArticle && (
                <DropdownMenuItem onClick={() => adapter.onOpenCreateArticle()} className="text-xs">
                  <PenSquare className="h-3.5 w-3.5 mr-2" /> Write Article
                </DropdownMenuItem>
              )}
              {newExtras.length > 0 && (
                <>
                  {(adapter.allows.upload || adapter.allows.writeArticle) && <DropdownMenuSeparator />}
                  {newExtras.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem
                        key={item.id}
                        onClick={item.onClick}
                        className="text-xs"
                      >
                        {Icon && <Icon className="h-3.5 w-3.5 mr-2" />}
                        {item.label}
                      </DropdownMenuItem>
                    );
                  })}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchInput}
            // The server's limit (LB6T2-BUG-07): a longer search is never sent.
            maxLength={DOCUMENT_SEARCH_MAX_LENGTH}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 h-9"
            aria-label="Search documents"
          />
        </div>
        <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as 'all' | DocumentKind)}>
          <SelectTrigger className="w-32 h-9 text-xs" aria-label="Filter by type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {/* Decision 19 — each option is a Document kind, from the shared
                schema in its order (L6-19); 'Synced' matches the rows of
                kind 'synced' (L19-14). */}
            {adapter.supportedKinds.map((kind) => (
              <SelectItem key={kind} value={kind}>{DOCUMENT_KIND_FILTER_LABEL[kind]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* The status options are the adapter's, from the schema its status
            speaks (L6-19): the tenant's ingestion status, 'pending' included;
            the System Library's lifecycle. */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger
            className={cn(adapter.showPublishLifecycle ? 'w-36' : 'w-28', 'h-9 text-xs')}
            aria-label="Filter by status"
          >
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{adapter.showPublishLifecycle ? 'All status' : 'All Status'}</SelectItem>
            {adapter.statusOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* The Tag filter (decision 60, LB6c-T1): the tenant's tags, each with
            the server's count; the server answers the filter. Drawn only
            where the adapter offers it (never on the System Library). */}
        {adapter.tagFilter ? <TagFilterSelect value={tagIdFilter} onChange={setTagIdFilter} /> : null}
        {/* #676 — When the route forces a folder via folderFilterOverride
            (tenant sidebar selection) the in-header Select hides; the
            sidebar IS the folder picker in that mode. Otherwise the
            Select drives the filter as before. */}
        {folderFilterOverride === undefined && (
          <Select value={folderFilter} onValueChange={setFolderFilter}>
            <SelectTrigger className="w-44 h-9 text-xs" aria-label="Filter by folder">
              <SelectValue placeholder="All folders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All folders</SelectItem>
              {folders.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Bulk action bar — multi-action (#10) */}
      {selectedIds.size > 0 && bulkActions.length > 0 && (
        <div className="rounded-lg border bg-primary/5 border-primary/30 px-3 py-2 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          {firstDisabledReason && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              {firstDisabledReason}
            </span>
          )}
          <div className="flex-1" />
          {bulkActions.map((action) => {
            const Icon = action.icon;
            // #679 — Destructive bulk actions (Delete) previously routed
            // directly to the adapter's onClick which used window.confirm
            // for a native prompt. Intercept here and stage the intent
            // through the in-app AlertDialog so the confirmation surface
            // is part of the DOM (visible to a11y tools + headless
            // Playwright). Non-destructive actions (Move, Publish-all)
            // keep their direct onClick path.
            const handleClick = () => {
              if (action.destructive) {
                setPendingDelete(buildDeleteIntent({ kind: 'bulk', docs: selectedDocs }));
                return;
              }
              action.onClick(selectedIds, selectedDocs);
            };
            return (
              <Button
                key={action.id}
                data-testid={`bulk-action-${action.id}`}
                size="sm"
                variant="outline"
                disabled={action.busy || action.disabled}
                onClick={handleClick}
                title={action.title ?? action.label}
                className={cn(
                  'gap-1.5',
                  action.destructive && 'text-destructive hover:text-destructive',
                )}
              >
                {action.busy
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : Icon && <Icon className="h-3.5 w-3.5" />}
                {action.label}
              </Button>
            );
          })}
          <Button size="sm" variant="ghost" onClick={clearSelection} title="Clear selection">
            Clear
          </Button>
        </div>
      )}

      {/* A read that failed while rows are on screen: they stay, and the
          failure is said with a retry (LB6T2-BUG-06). */}
      {loadMoreError ? (
        <div
          role="alert"
          data-testid="library-list-refresh-error"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
        >
          The documents could not all be loaded: {loadMoreError.message}
          {retry ? (
            <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs" onClick={() => retry()} title="Load the documents again">
              Retry
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* Unified table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              {/* C-A11Y cluster (issue #692 empty-table-header minor):
                  Give the bulk-select column an explicit aria-label so axe
                  doesn't flag the header as empty. The checkbox already
                  carries its own aria-label for AT users. */}
              {adapter.allows.bulkSelect && (
                <th className="w-10 px-3 py-2.5 text-left" aria-label="Select rows">
                  <Checkbox
                    aria-label="Select all"
                    checked={allSelectedOnPage}
                    onCheckedChange={(c) => handleToggleAll(!!c)}
                  />
                </th>
              )}
              {adapter.showStarColumn && (
                <th className="w-10 px-3 py-2.5 text-left" aria-label="Favorites column" />
              )}
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">
                {sortable('Document', 'title')}
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">
                {sortable('Type', 'kind')}
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">
                Folder
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">
                {sortable(adapter.showPublishLifecycle ? 'Publish' : 'Status', 'status')}
              </th>
              {adapter.showAuthorColumn && (
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">
                  {sortable('Author', 'authorLabel')}
                </th>
              )}
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">
                {sortable('Updated', 'updatedAt')}
              </th>
              {/* C-A11Y cluster (issue #692): give the row-actions column
                  an accessible name so axe doesn't flag empty-table-header. */}
              <th className="w-10 px-3 py-2.5" aria-label="Row actions"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {docs.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="p-12 text-center">
                  <FileText className="h-8 w-8 text-muted-foreground/20 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {filtersActive ? adapter.emptyFilterMessage : adapter.emptyStateMessage}
                  </p>
                </td>
              </tr>
            ) : (
              <>
                {visible.map((d) => (
                  <tr
                    key={d.id}
                    className={cn(
                      'hover:bg-muted/30 transition-colors group/row',
                      selectedIds.has(d.id) && 'bg-primary/5',
                    )}
                  >
                    {adapter.allows.bulkSelect && (
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        aria-label={
                          adapter.isSelectable(d)
                            ? `Select ${d.title}`
                            : `${d.title} is not selectable`
                        }
                        checked={selectedIds.has(d.id)}
                        disabled={!adapter.isSelectable(d)}
                        onCheckedChange={() => toggleRowSelected(d)}
                        title={
                          adapter.isSelectable(d)
                            ? undefined
                            : 'Wiki articles use the editor for moves/deletes'
                        }
                      />
                    </td>
                    )}
                    {adapter.showStarColumn && (
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <span className="inline-flex">
                          {/* Every row is a document (decision 19) — a file, a
                              synced document or an article — and is starred
                              as one (decision 38). */}
                          <StarButton item={{ id: d.id, kind: 'document' }} showOnHoverOnly />
                        </span>
                      </td>
                    )}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <DocumentKindIcon kind={d.kind} className="h-3.5 w-3.5 flex-shrink-0" />
                        <button
                          className="text-left hover:underline font-medium"
                          onClick={() => adapter.onEditRow(d, query)}
                          title={d.title}
                        >
                          {d.title}
                        </button>
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate ml-5">
                        {adapter.renderTitleSubline(d)}
                      </div>
                    </td>
                    <td className="px-3 py-2.5"><DocumentKindBadge kind={d.kind} /></td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">
                      {d.folderName || <span className="italic">root</span>}
                    </td>
                    <td className="px-3 py-2.5">{adapter.renderStatusCell(d)}</td>
                    {adapter.showAuthorColumn && (
                      <td className="px-3 py-2.5 text-xs text-muted-foreground truncate">
                        {d.authorLabel}
                      </td>
                    )}
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">
                      {timeAgo(d.updatedAt)}
                    </td>
                    <td className="px-3 py-2.5">
                      {rowHasActions(d) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label="Document actions"
                            title="More actions"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {/* #712 — View row item (uploaded + external only).
                              Article rows already open the reader on title
                              click, so View would be a duplicate affordance
                              there. Gate via the pure predicate so the test
                              suite + the body share one source of truth. */}
                          {shouldRenderViewItem(adapter, d) && (
                            <DropdownMenuItem
                              onClick={() => adapter.onViewRow!(d)}
                              className="text-xs"
                            >
                              <Eye className="h-3.5 w-3.5 mr-2" /> View
                            </DropdownMenuItem>
                          )}
                          {adapter.allows.edit(d) && (
                            <DropdownMenuItem
                              onClick={() => adapter.onEditRow(d, query)}
                              className="text-xs"
                            >
                              <Edit className="h-3.5 w-3.5 mr-2" />
                              {d.kind === 'article' ? 'Edit' : 'Edit metadata'}
                            </DropdownMenuItem>
                          )}
                          {/* #712 — Download row item. Articles are HTML and
                              get an Export affordance in the reader (#689);
                              uploaded + external rows expose the original
                              file via the connector / knowledge file route. */}
                          {shouldRenderDownloadItem(adapter, d) && (
                            <DropdownMenuItem
                              onClick={() => adapter.onDownloadRow!(d)}
                              className="text-xs"
                            >
                              <Download className="h-3.5 w-3.5 mr-2" /> Download
                            </DropdownMenuItem>
                          )}
                          {/* Publish row actions — gated only on the adapter's
                              opt-in handlers + the doc's current status. We
                              dropped the prior `showPublishLifecycle` gate so
                              tenant articles (which have a `draft|published`
                              status the same way sysadmin docs do, but render
                              a different status pill) can also Publish from
                              the row dropdown. See P1 #1 in the Round 1
                              Library audit (2026-05-13). */}
                          {adapter.onPublishRow && adapter.allows.publish(d) && d.status === 'draft' && (
                            <DropdownMenuItem
                              onClick={() => adapter.onPublishRow!(d)}
                              className="text-xs"
                              disabled={hasFailedIngest(d)}
                            >
                              Publish
                            </DropdownMenuItem>
                          )}
                          {adapter.onUnpublishRow && adapter.allows.publish(d) && d.status === 'published' && (
                            <DropdownMenuItem
                              onClick={() => adapter.onUnpublishRow!(d)}
                              className="text-xs"
                            >
                              Unpublish…
                            </DropdownMenuItem>
                          )}
                          {adapter.onPublishRow && adapter.allows.publish(d) && d.status === 'deprecated' && (
                            <DropdownMenuItem
                              onClick={() => adapter.onPublishRow!(d)}
                              className="text-xs"
                            >
                              Re-publish
                            </DropdownMenuItem>
                          )}
                          {/* Share row (#7) — every kind: an article is a document
                              (decision 19) and a share restricts it as it does a
                              file (decision 22; LX-03). The adapter wires it only
                              for a person who holds the share key. */}
                          {adapter.onShareRow && (
                            <DropdownMenuItem
                              onClick={() => adapter.onShareRow!(d)}
                              className="text-xs"
                            >
                              <Share2 className="h-3.5 w-3.5 mr-2" /> Share
                            </DropdownMenuItem>
                          )}
                          {/* Move row — tenant only (#9 row-level entry to dialog) */}
                          {adapter.onMoveRow && (d.kind === 'file' || d.kind === 'synced') && (
                            <DropdownMenuItem
                              onClick={() => adapter.onMoveRow!(d)}
                              className="text-xs"
                            >
                              Move to folder…
                            </DropdownMenuItem>
                          )}
                          {adapter.allows.delete(d) && (
                          <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            // #677 — Pre-fix the click delegated to the
                            // adapter's onDeleteRow which gated on a native
                            // window.confirm. Headless Playwright + a11y
                            // tools could not see the prompt and the menu
                            // appeared to close with no feedback. Route the
                            // trigger through a state-driven AlertDialog
                            // (rendered below) so the confirmation surface
                            // is part of the DOM.
                            onClick={() => setPendingDelete(buildDeleteIntent({ kind: 'row', doc: d }))}
                            className="text-xs text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                          </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      )}
                    </td>
                  </tr>
                ))}
                {hasMore && (
                  <tr>
                    <td colSpan={colCount} className="p-3 text-center border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-muted-foreground"
                        onClick={() => loadMore()}
                        disabled={loadingMore}
                        title="Load the next page of documents"
                      >
                        {loadingMore ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                        {remaining !== null ? `Show more (${remaining} remaining)` : 'Show more'}
                      </Button>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      {/* #677 + #679 — Shared destructive-action AlertDialog.
          One dialog services both row-level Delete (single doc) and
          bulk-bar Delete (N docs). The `pendingDelete` intent envelope
          tells the dialog what title / body / count to show + which ids
          to send to the adapter on Confirm. Native window.confirm was
          replaced because (a) headless Playwright doesn't see it,
          (b) axe / NVDA can't introspect it, and (c) the unstyled
          native dialog looked like an unrelated browser warning. */}
      <DeleteConfirmDialog
        intent={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={(intent) => {
          setPendingDelete(null);
          if (intent.scope === 'row') {
            // Row scope — adapter.onDeleteRow expects the source doc
            // (it routes article vs uploaded delete internally).
            const doc = docs.find((d) => d.id === intent.ids[0]);
            if (doc) adapter.onDeleteRow(doc);
          } else {
            // Bulk scope — re-derive the action to fire. The adapter
            // contributes the destructive action with its onClick
            // wired to the route's bulk delete handler.
            const action = bulkActions.find((a) => a.destructive);
            if (action) {
              const targetDocs = docs.filter((d) => intent.ids.includes(d.id));
              action.onClick(new Set(intent.ids), targetDocs);
            }
          }
        }}
      />
    </div>
  );
}

/**
 * The Documents page's Tag filter (decision 60, LB6c-T1): the tenant's tags
 * from the ONE tag query (useWikiTags — the key the page's tag manager and the
 * edit forms' tag field read), each with the server's count of the documents
 * this person may see that carry it. Choosing one asks the source again with
 * the tag's id; the server lists every kind carrying it. A failed read of the
 * tag list is shown with a retry in the Select's place — never an empty "All
 * tags" (principle 4); the document list itself is unaffected. A filter is a
 * read: no key beyond the list's own.
 *
 * LB6cT1-REV-04 — the filter is never on without a control that shows it: a
 * failed REFETCH keeps the Select on the cached tags (the error replaces it
 * only when there are none), and a chosen tag that leaves the catalogue
 * (deleted or merged) resets the filter to "All tags".
 */
function TagFilterSelect({ value, onChange }: { value: string; onChange: (tagId: string) => void }) {
  const tagsQ = useWikiTags();
  const inCatalogue = tagFilterInCatalogue(value, tagsQ.data);
  useEffect(() => {
    if (inCatalogue !== value) onChange(inCatalogue);
  }, [inCatalogue, value, onChange]);
  if (tagsQ.isError && !tagsQ.data) {
    return (
      <div role="alert" data-testid="tag-filter-error" className="flex items-center gap-x-2 text-xs text-destructive">
        <span>The tags could not be loaded.</span>
        <button type="button" className="underline" onClick={() => void tagsQ.refetch()} title="Load the tag list again">
          Retry
        </button>
      </div>
    );
  }
  const tags = tagsQ.data;
  return (
    <Select value={inCatalogue} onValueChange={onChange} disabled={!tags}>
      <SelectTrigger className="w-36 h-9 text-xs" aria-label="Filter by tag">
        <SelectValue placeholder={tags ? 'All tags' : 'Loading tags…'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All tags</SelectItem>
        {(tags ?? []).map((t) => (
          <SelectItem key={t.id} value={t.id}>{`${t.name} (${t.count})`}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
